<template>
  <div class="main-header">
    <nav class="main-header__links links lt-1152">
      <span class="icon links__burger-icon" @click="toggleMobileMenu">{{
        $options.icon.menu
      }}</span>

      <transition name="slide-from-left">
        <mobile-header-menu
          v-if="isMobileMenuOpen"
          ref="mobileMenu"
          :sections="sections"
          @close="toggleMobileMenu"
        />
      </transition>
    </nav>

    <nav class="main-header__links links gte-1152">
      <router-link
        v-for="section of sections"
        :key="section.labelKey"
        :to="section.route"
        class="links__link u-typography-helvetica u-text u-text--link u-text--s"
      >
        {{ $t(section.labelKey) }}
      </router-link>
    </nav>

    <div class="main-header__actions actions">
      <img class="actions__cart" :src="cartIcon" alt="cart" @click="onToggleCart" />

      <span
        v-show="!isCartEmpty"
        class="actions__products-count u-typography-helvetica u-text u-text--link u-text--s"
      >
        {{ cartProductsCount }}
      </span>

      <locale-select />
    </div>

    <div class="main-header__progress" :style="progressStyles"></div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';

import emptyCartIcon from '@/assets/images/icons/basket-empty-icon.svg';
import filledCartIcon from '@/assets/images/icons/basket-filled-icon.svg';

import { toggleDocumentScroll } from '@/utils/common';

import { ICON, ICON_VALUE } from '@/constants/icons';
import { types } from '@/store/types';

export default {
  name: 'MainHeader',
  components: {
    LocaleSelect: () => import(/* webpackPreload: true */ '@/components/header/LocaleSelect.vue'),
    MobileHeaderMenu: () => import('@/components/header/MobileHeaderMenu.vue')
  },
  icon: {
    menu: ICON_VALUE[ICON.MENU]
  },
  props: {
    progress: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      isMobileMenuOpen: false
    };
  },
  computed: {
    ...mapGetters({
      cartProductsCount: types.getters.CART_PRODUCTS_COUNT,
      sections: types.getters.MAIN_PAGE_SECTIONS
    }),
    cartIcon() {
      if (this.isCartEmpty) {
        return emptyCartIcon;
      }

      return filledCartIcon;
    },
    isCartEmpty() {
      return this.cartProductsCount === 0;
    },
    progressStyles() {
      return {
        '--progress-width': `${this.progress}%`
      };
    }
  },
  watch: {
    isMobileMenuOpen: 'togglePageScroll'
  },
  beforeDestroy() {
    this.togglePageScroll(false);
  },
  methods: {
    ...mapMutations({
      onToggleCart: types.mutations.TOGGLE_OPEN_CART
    }),
    toggleMobileMenu() {
      this.isMobileMenuOpen = !this.isMobileMenuOpen;
    },
    togglePageScroll(isOpen) {
      toggleDocumentScroll(this.$refs.mobileMenu, isOpen);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '~@/assets/scss/icon.scss';
@import '~@/assets/scss/colors.scss';

.main-header {
  border-bottom: 1px solid var(--u-color-grey-200);
  display: flex;
  padding: 16px;
  height: 60px;
  background-color: white;
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;

  &__actions {
    margin-inline-start: auto;
  }

  &__progress {
    position: absolute;
    left: 0;
    bottom: -1px;
    width: var(--progress-width);
    height: 0;
    border-bottom: 1px solid black;
    z-index: 0;
  }
}

.links {
  display: flex;
  align-items: center;

  &__burger-icon {
    font-size: 31px;
    cursor: pointer;
  }

  &__link {
    color: $tuatara-color;
    text-decoration: none;
  }
}

.actions {
  display: flex;
  align-items: center;

  &__cart {
    display: block;
    width: 24px;
    cursor: pointer;
    margin-inline-end: 16px;
  }

  &__products-count {
    margin-inline-end: 32px;
  }
}

@media (min-width: 600px) {
  .main-header {
    padding: 24px;
    height: 70px;
  }
}

@media (min-width: 1152px) {
  .main-header {
    padding: 24px 40px;
  }

  .links {
    &__link {
      margin-inline-end: 24px;
      cursor: pointer;
    }
  }
}

@media (min-width: 1440px) {
  .links {
    &__link {
      margin-inline-end: 32px;
    }
  }
}
</style>
